import { GoogleMap } from '@react-google-maps/api';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { useGoogleMaps } from '@/providers/GoogleMapsProvider';
import { Territory } from '@/types/territories/territory.type';

const TERRITORY_LOAD_ZOOM = 8;

import { TerritoriesBoard } from './TerritoriesBoard';

export interface MapRef {
  flyTo: (center: { lat: number; lng: number }, zoom?: number) => void;
}

interface MapProps {
  zoom?: number;
  center: { lat: number; lng: number };
  onTerritoryClick?: (territory: Territory) => void;
}

const useDebounce = <T,>(value: T, delay: number = 500): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const normalizeBounds = (bounds: google.maps.LatLngBounds) => {
  const rawNeLng = bounds.getNorthEast().lng();
  let swLng = bounds.getSouthWest().lng();

  // When the viewport crosses the antimeridian (180°/-180°), Google Maps
  // returns sw.lng > ne.lng (e.g. sw.lng=161° instead of -199°).
  // Normalize by subtracting 360, then clamp to the valid -180..180 range.
  if (swLng > rawNeLng) {
    swLng = Math.max(-180, swLng - 360);
  }

  return {
    ne: {
      lat: bounds.getNorthEast().lat(),
      lng: Math.min(180, rawNeLng),
    },
    sw: {
      lat: bounds.getSouthWest().lat(),
      lng: swLng,
    },
  };
};

export const Map = forwardRef<MapRef, MapProps>(
  ({ zoom = 4, center, onTerritoryClick }, ref) => {
    const { isLoaded, loadError } = useGoogleMaps();

    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [options, setOptions] = useState<google.maps.MapOptions>({
      zoomControl: true,
      disableDefaultUI: true,
    });
    const [currentZoom, setCurrentZoom] = useState<number>(zoom);
    const [currentCenter, setCurrentCenter] = useState<{
      lat: number;
      lng: number;
    }>(center);
    const [currentBounds, setCurrentBounds] = useState<{
      ne: { lat: number; lng: number };
      sw: { lat: number; lng: number };
    }>({
      ne: { lat: 0, lng: 0 },
      sw: { lat: 0, lng: 0 },
    });

    const debouncedZoom = useDebounce(currentZoom, 100);
    const debouncedBounds = useDebounce(currentBounds, 100);

    useImperativeHandle(
      ref,
      () => ({
        flyTo: (center, zoom) => {
          if (map) {
            const targetZoom =
              zoom ??
              (currentZoom >= TERRITORY_LOAD_ZOOM
                ? currentZoom
                : TERRITORY_LOAD_ZOOM);
            map.panTo(center);
            map.setZoom(targetZoom);
          }
        },
      }),
      [map, currentZoom]
    );

    if (loadError) {
      console.error('Google Maps load error:', loadError);
      return <div>Error loading Google Maps</div>;
    }

    if (!isLoaded) {
      return null;
    }

    return (
      <GoogleMap
        zoom={currentZoom}
        center={currentCenter}
        options={options}
        mapContainerStyle={{ width: '100%', height: '100%', borderRadius: 8 }}
        onLoad={(mapInstance) => {
          try {
            setMap(mapInstance);

            const zoom = mapInstance.getZoom();
            if (zoom) setCurrentZoom(zoom);

            const center = mapInstance.getCenter()?.toJSON();
            if (center) setCurrentCenter(center);

            // Capture initial bounds immediately after render so the first API
            // call uses real viewport coordinates instead of the {0,0} defaults.
            setTimeout(() => {
              const bounds = mapInstance.getBounds();
              if (bounds) {
                setCurrentBounds(normalizeBounds(bounds));
              }
            }, 100);

            setOptions({
              ...options,
              zoomControlOptions: {
                position: google.maps.ControlPosition.LEFT_BOTTOM,
              },
            });
          } catch (error) {
            console.error('Error in GoogleMap onLoad:', error);
          }
        }}
        onIdle={() => {
          if (map) {
            const newZoom = map.getZoom() ?? zoom;
            const newCenter = map.getCenter()?.toJSON() as {
              lat: number;
              lng: number;
            };

            const hasSignificantZoomChange =
              Math.abs(currentZoom - newZoom) > 0.5;
            const hasSignificantCenterChange =
              Math.abs(newCenter.lat - currentCenter.lat) > 0.1 ||
              Math.abs(newCenter.lng - currentCenter.lng) > 0.1;

            if (hasSignificantZoomChange) {
              setCurrentZoom(newZoom);
            }
            if (hasSignificantCenterChange) {
              setCurrentCenter(newCenter);
            }

            const bounds = map.getBounds();
            if (bounds) {
              const newBounds = normalizeBounds(bounds);

              const hasSignificantBoundsChange =
                Math.abs(newBounds.ne.lat - currentBounds.ne.lat) > 0.1 ||
                Math.abs(newBounds.ne.lng - currentBounds.ne.lng) > 0.1 ||
                Math.abs(newBounds.sw.lat - currentBounds.sw.lat) > 0.1 ||
                Math.abs(newBounds.sw.lng - currentBounds.sw.lng) > 0.1;

              if (hasSignificantBoundsChange) {
                setCurrentBounds(newBounds);
              }
            }
          }
        }}
      >
        <TerritoriesBoard
          zoom={debouncedZoom}
          bounds={debouncedBounds}
          onTerritoryClick={onTerritoryClick}
        />
      </GoogleMap>
    );
  }
);

Map.displayName = 'Map';
